<template>
  <div
    v-if="comment"
    class="comment"
  >
    <div class="comment-author comment__author">
      <div class="comment-author__avatar">
        <img
          :src="avatar"
          alt="Аватар"
          class="comment-author__image"
        />
      </div>
      <div class="comment-author__info">
        <div class="comment-author__name">{{ comment.author }}</div>
        <time
          class="comment-author__date"
          :datetime="dateMoscow"
          >{{ getTime }}</time
        >
      </div>
      <div class="comment-author__rating">
        <span class="comment-author__like">{{ likes }}</span>
        <span class="comment-author__delimiter">&nbsp;/&nbsp;</span>
        <span class="comment-author__dislike">{{ dislikes }}</span>
      </div>
    </div>
    <p
      class="comment__content"
      v-html="comment.content"
    />

    <div
      v-if="hasFiles"
      class="files"
    >
      <template v-if="showFilePreview">
        <img
          v-for="file in commentFiles"
          :key="file.id"
          :src="file.path"
          alt=""
        />
      </template>
      <div
        v-else
        class="fake-preview"
      >
        Изображение
      </div>
    </div>

    <BHLink
      :href="articleLink"
      class="comment__article-title"
      colorless
      @click="handleCommentClick(articleLink)"
    >
      <span v-html="comment.post_title" />
    </BHLink>
    <a
      v-if="articleLink"
      :href="commentLink"
      aria-label="Перейти к комментарию"
      class="comment__link"
      @click="handleToCommentClick(commentLink)"
    />
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import BHLink from '../BHLink.vue';
  import type { TWidgetComment } from '~/types/common';
  import { getTimePassed } from '~/libs/getTimePassed';
  import defaultAvatar from '~/assets/images/raster/default-comment-avatar.jpg';
  import { TALKER_API_URL } from '~/constants';
  import { convertDateToMoscowString, getCommentDate } from '@devhacker/shared/utils/date';

  export default defineNuxtComponent({
    name: 'CommentPreview',
    components: { BHLink },
    props: {
      comment: {
        type: Object as PropType<TWidgetComment>,
        required: true,
      },
      showFilePreview: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['commentClick', 'toCommentClick'],

    computed: {
      articleLink(): string {
        return this.comment.post_link || `/${this.comment.post?.slug}`;
      },

      commentId(): number | undefined {
        return this.comment.comment_id || this.comment.id;
      },

      likes(): string {
        const likes = this.comment.users_liked_cnt || this.comment.likes;
        return likes ? `+${likes}` : '0';
      },

      dislikes(): string {
        const dislikes = this.comment.users_disliked_cnt || this.comment.dislikes;
        return dislikes ? `-${dislikes}` : '0';
      },

      avatar() {
        let avatarUrl = defaultAvatar;

        if (this.comment.avatar?.url && this.comment.avatar?.url !== 'unknown') {
          avatarUrl = this.comment.avatar.url;
        }

        if (this.comment.author_avatar && this.comment.author_avatar !== 'unknown') {
          avatarUrl = this.comment.author_avatar;
        }
        return avatarUrl;
      },

      commentLink(): string {
        return `${this.articleLink}#comment-${this.commentId}`;
      },

      getTime(): string {
        const time = useState(`COMMENT_TIME_AGO_${this.commentId}`, () =>
          getTimePassed(this.comment.date_gmt),
        );
        return time.value;
      },

      dateMoscow() {
        return convertDateToMoscowString(getCommentDate(this.comment.date_gmt));
      },

      commentFiles() {
        return this.comment.files?.map((file) => ({
          ...file,
          path: `${TALKER_API_URL}/${file.path.replace('public/', 'storage/')}`,
        }));
      },

      hasFiles(): boolean {
        return Array.isArray(this.comment.files) ? this.comment.files.length > 0 : false;
      },
    },

    methods: {
      handleCommentClick(link: string) {
        this.$emit('commentClick', link);
      },
      handleToCommentClick(link: string) {
        this.$emit('toCommentClick', link);
      },
    },
  });
</script>

<style lang="scss" scoped>
  .comment {
    position: relative;
    cursor: pointer;
    padding: $spacing-4 $spacing-6;
    border-radius: 28px;

    @include hover {
      &::before {
        position: absolute;
        content: '';
        left: 0;
        top: 0;
        border: 4px solid $hover-color--1;
        border-radius: 28px;
        height: 100%;
        width: 100%;
      }
    }

    &__link {
      position: absolute;
      z-index: 2;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }

    &__content {
      margin-top: $spacing-2;
      @include textElipsis(
        (
          'lc': 3,
        )
      );
      max-height: 72px;
      @include normalText;

      &:empty {
        display: none;
      }
    }

    &__article-title {
      z-index: 4;
      position: relative;
      display: block;
      margin-top: $spacing-2;
      @include smallText;
      color: $icon-color--1;
      @include hover {
        cursor: pointer;
        color: $hover-color--1;
      }
    }
  }

  .comment-author {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;

    &__avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      overflow: hidden;
      margin-right: $spacing-2;
    }

    &__name {
      @include font(
        (
          'fw': 500,
          'fs': 16px,
          'lh': 24px,
        )
      );

      @include textElipsis(
        (
          'lc': 1,
        )
      );
      //max-width: 160px;
    }

    &__date {
      @include font(
        (
          'fs': 12px,
          'lh': 18px,
        )
      );
      color: $icon-color--1;
    }

    &__image {
      border-radius: 50%;
      height: 100%;
      width: 100%;
      object-fit: cover;
    }

    &__rating {
      font-family: $text-font--1;
      font-weight: 500;
      margin-left: auto;
      padding-left: 16px;

      @include font(
        (
          'fs': 14px,
          'lh': 16px,
        )
      );
    }

    &__like {
      color: #1c8920;
    }

    &__delimiter {
      color: #e7e7e7;
    }

    &__dislike {
      color: #ed0000;
    }
  }

  .files {
    display: flex;
    gap: 8px;
    margin-top: 8px;

    img {
      display: block;
      height: 224px;
      max-width: 100%;
      object-fit: contain;
    }
  }

  .fake-preview {
    font-size: 16px;
    line-height: 24px;
    padding: 16px 8px;
    background: #fbf8f8;
    color: #292236;
  }
</style>
